export type SectionParserCallbacks = {
  onSectionComplete: (sectionXml: string) => void;
  onPageStart?: () => void;
  onPageComplete?: () => void;
  /** Called with a self-closing page tag (e.g., `<page bg="#fff" />`) for parsing with xmlToCraftJson */
  onPageTag?: (pageTagXml: string) => void;
};

/**
 * Streaming XML parser that detects complete <section>...</section> elements
 * as they are received from a streaming source.
 *
 * Handles nested elements by tracking tag depth. When a complete section
 * is detected, calls onSectionComplete with the full section XML.
 */
export class StreamingXmlSectionParser {
  private buffer = '';
  private callbacks: SectionParserCallbacks;
  private pageStarted = false;
  private pageAttributesEmitted = false;

  constructor(callbacks: SectionParserCallbacks) {
    this.callbacks = callbacks;
  }

  /**
   * Append a chunk of text to the buffer and process any complete sections
   */
  append(chunk: string): void {
    this.buffer += chunk;
    this.processBuffer();
  }

  /**
   * Process any remaining content in the buffer (call when streaming is complete)
   */
  finalize(): void {
    // Process any remaining complete sections
    this.processBuffer();

    // Check for page end
    if (this.buffer.includes('</page>')) {
      this.callbacks.onPageComplete?.();
    }
  }

  private processBuffer(): void {
    // Check for page start (use boundary-aware regex to avoid matching tags like <pageContent>)
    if (!this.pageStarted && /<page(?:\s|>)/.test(this.buffer)) {
      this.pageStarted = true;
      this.callbacks.onPageStart?.();
    }

    // Extract page tag as soon as the opening tag is complete
    if (this.pageStarted && !this.pageAttributesEmitted) {
      const pageTagXml = this.extractPageTag();
      if (pageTagXml !== null) {
        this.pageAttributesEmitted = true;
        this.callbacks.onPageTag?.(pageTagXml);
      }
    }

    // Look for complete sections
    this.extractCompleteSections();
  }

  /**
   * Extract the page opening tag and convert it to a self-closing tag.
   * Returns null if the opening tag is not yet complete.
   * Example: `<page bg="#fff">` becomes `<page bg="#fff" />`
   */
  private extractPageTag(): string | null {
    // Find the <page opening tag
    const pageTagStart = this.buffer.indexOf('<page');
    if (pageTagStart === -1) return null;

    // Find the closing > of the opening tag
    // Need to handle quotes properly to avoid matching > inside attribute values
    let i = pageTagStart + 5; // Skip past '<page'
    let inQuote: string | null = null;

    while (i < this.buffer.length) {
      const char = this.buffer[i];

      if (inQuote) {
        if (char === inQuote) {
          inQuote = null;
        }
      } else {
        if (char === '"' || char === "'") {
          inQuote = char;
        } else if (char === '>') {
          // Found the end of the opening tag
          // Extract content and convert to self-closing tag
          const tagContent = this.buffer.slice(pageTagStart, i);
          return `${tagContent} />`;
        }
      }

      i++;
    }

    // Opening tag not yet complete
    return null;
  }

  private extractCompleteSections(): void {
    // Pattern to find <section start
    const sectionStartPattern = /<section(?:\s|>)/;

    let match = sectionStartPattern.exec(this.buffer);

    while (match) {
      const startIndex = match.index;

      // Find the matching </section> tag
      const endResult = this.findMatchingClosingTag(this.buffer, startIndex);

      if (endResult === null) {
        // Incomplete section, wait for more data
        break;
      }

      // Extract the complete section (escaping is handled by xmlToCraftJson)
      const sectionXml = this.buffer.slice(startIndex, endResult.endIndex);

      // Call callback with the section
      this.callbacks.onSectionComplete(sectionXml);

      // Remove processed content from buffer
      this.buffer = this.buffer.slice(endResult.endIndex);

      // Look for next section
      match = sectionStartPattern.exec(this.buffer);
    }
  }

  /**
   * Find the matching closing </section> tag, accounting for nested elements
   * Returns the end index (after the closing tag) or null if not found
   */
  private findMatchingClosingTag(text: string, startIndex: number): { endIndex: number } | null {
    let depth = 0;
    let i = startIndex;

    while (i < text.length) {
      // Skip to next '<'
      const nextLt = text.indexOf('<', i);
      if (nextLt === -1) return null;
      i = nextLt;

      // Check what kind of tag this is
      if (text.startsWith('<!--', i)) {
        // HTML comment - skip to end
        const commentEnd = text.indexOf('-->', i);
        if (commentEnd === -1) return null;
        i = commentEnd + 3;
        continue;
      }

      if (text.startsWith('<![CDATA[', i)) {
        // CDATA section - skip to end
        const cdataEnd = text.indexOf(']]>', i);
        if (cdataEnd === -1) return null;
        i = cdataEnd + 3;
        continue;
      }

      // Find the end of this tag
      const tagEnd = this.findTagEnd(text, i);
      if (tagEnd === -1) return null;

      const tagContent = text.slice(i + 1, tagEnd);
      const isSelfClosing = tagContent.endsWith('/');
      const isClosingTag = tagContent.startsWith('/');

      if (isClosingTag) {
        // Extract tag name from closing tag
        const tagName = tagContent.slice(1).split(/[\s>]/)[0];

        if (tagName === 'section') {
          depth--;
          if (depth === 0) {
            return { endIndex: tagEnd + 1 };
          }
        }
      } else if (!isSelfClosing) {
        // Opening tag
        const tagName = tagContent.split(/[\s/>]/)[0];

        if (tagName === 'section') {
          depth++;
        }
      }
      // Self-closing tags don't affect depth

      i = tagEnd + 1;
    }

    return null;
  }

  /**
   * Find the index of the '>' that closes a tag, handling attributes with quotes
   */
  private findTagEnd(text: string, startIndex: number): number {
    let i = startIndex + 1;
    let inQuote: string | null = null;

    while (i < text.length) {
      const char = text[i];

      if (inQuote) {
        if (char === inQuote) {
          inQuote = null;
        }
      } else {
        if (char === '"' || char === "'") {
          inQuote = char;
        } else if (char === '>') {
          return i;
        }
      }

      i++;
    }

    return -1;
  }

  /**
   * Get any remaining unparsed content in the buffer
   */
  getBuffer(): string {
    return this.buffer;
  }

  /**
   * Clear the buffer
   */
  clear(): void {
    this.buffer = '';
    this.pageStarted = false;
    this.pageAttributesEmitted = false;
  }
}
